import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

type OverlayPanelProps = {
  isOpen: boolean
  onClose: () => void
  ariaLabel: string
  backgroundImage?: string
  className?: string
  contentClassName?: string
  backdropClassName?: string
  closeButtonClassName?: string
  children: ReactNode
}

function OverlayPanel({
  isOpen,
  onClose,
  ariaLabel,
  backgroundImage,
  className,
  contentClassName,
  backdropClassName,
  closeButtonClassName,
  children,
}: OverlayPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    closeButtonRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return

      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      )

      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        last.focus()
        event.preventDefault()
      } else if (!event.shiftKey && active === last) {
        first.focus()
        event.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className={`fixed inset-0 z-50 ${className ?? ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={onClose}
    >
      <div className="absolute inset-0" aria-hidden="true">
        {backgroundImage ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        ) : null}
        <div
          className={`absolute inset-0 ${backdropClassName ?? 'bg-black/30 backdrop-blur-[12px]'}`}
        />
      </div>

      <div
        ref={panelRef}
        className={`relative z-10 mx-auto h-full w-full max-w-page px-page-gutter xl:px-content-inset 3xl:px-content-inset-wide ${
          contentClassName ?? ''
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onClose()
          }}
          aria-label="Close panel"
          className={`group absolute right-[24px] top-[24px] z-20 flex size-[64px] cursor-pointer items-center justify-center rounded-full !bg-brand-greenBright !text-brand-green outline-none transition-transform duration-200 hover:scale-[1.05] focus-visible:scale-[1.05] focus-visible:ring-2 focus-visible:ring-white/70 md:right-[72px] md:top-[72px] md:size-[88px] ${closeButtonClassName ?? ''}`}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 32 32"
            className="size-[20px] md:size-[28px]"
            fill="none"
          >
            <path
              d="M8 8L24 24M24 8L8 24"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {children}
      </div>
    </div>
  )
}

export default OverlayPanel
